import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const ChaseSection = styled.div`
  width: 100%;
  margin-top: 12px;
`;

const ChaseLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const Track = styled.div`
  position: relative;
  width: 100%;
  height: 44px;
  background: rgba(0, 0, 0, 0.08);
  border-radius: 6px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  overflow: visible;
`;

const TrackDots = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 4px;
  transform: translateY(-50%);
  background: repeating-linear-gradient(
    90deg,
    rgba(0, 0, 0, 0.12) 0px,
    rgba(0, 0, 0, 0.12) 6px,
    transparent 6px,
    transparent 18px
  );
  border-radius: 2px;
`;

const Door = styled(motion.div)`
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 32px;
  height: 40px;
  z-index: 2;

  .door-frame {
    width: 100%;
    height: 100%;
    background: linear-gradient(180deg, #8b4513 0%, #654321 100%);
    border-radius: 16px 16px 4px 4px;
    border: 2px solid #daa520;
    box-shadow: 0 0 8px rgba(218, 165, 32, 0.4);
    position: relative;
    overflow: hidden;
  }

  .door-opening {
    position: absolute;
    bottom: 4px;
    left: 50%;
    transform: translateX(-50%);
    width: 20px;
    height: 26px;
    background: linear-gradient(180deg, #ffd700 0%, #ff8c00 100%);
    border-radius: 10px 10px 2px 2px;
    box-shadow: inset 0 0 6px rgba(255, 255, 255, 0.4);
  }

  .door-knob {
    position: absolute;
    right: 5px;
    top: 50%;
    width: 4px;
    height: 4px;
    background: #ffd700;
    border-radius: 50%;
  }
`;

const Character = styled(motion.div)`
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 3;
`;

const PacManSprite = styled.div`
  width: 28px;
  height: 28px;
  position: relative;

  .body {
    width: 28px;
    height: 28px;
    background: #ffcc00;
    border-radius: 50%;
    position: relative;
    box-shadow: 0 0 6px rgba(255, 204, 0, 0.5);
    animation: ${props => props.$active ? 'chomp 0.3s infinite alternate' : 'none'};
  }

  .eye {
    position: absolute;
    top: 5px;
    right: 7px;
    width: 5px;
    height: 5px;
    background: #1a1a2e;
    border-radius: 50%;
    z-index: 2;
  }

  @keyframes chomp {
    0% { clip-path: polygon(50% 50%, 100% 5%, 100% 95%); }
    100% { clip-path: polygon(50% 50%, 100% 20%, 100% 80%); }
  }
`;

const HumanSprite = styled.div`
  width: 24px;
  height: 32px;
  position: relative;
  animation: ${props => props.$running ? 'run 0.4s infinite alternate' : 'none'};

  .head {
    width: 12px;
    height: 12px;
    background: #f4a460;
    border-radius: 50%;
    margin: 0 auto 2px;
    border: 2px solid #d2691e;
  }

  .body {
    width: 10px;
    height: 12px;
    background: #4a90e2;
    border-radius: 4px 4px 2px 2px;
    margin: 0 auto;
  }

  .legs {
    display: flex;
    justify-content: center;
    gap: 2px;
    margin-top: 1px;
  }

  .leg {
    width: 4px;
    height: 7px;
    background: #2c3e50;
    border-radius: 2px;
  }

  @keyframes run {
    0% { transform: translateY(0); }
    100% { transform: translateY(-2px); }
  }
`;

const StatusMessage = styled(motion.div)`
  text-align: center;
  margin-top: 6px;
  font-size: 0.8rem;
  font-weight: 700;
  color: ${props => props.$escaped ? '#22c55e' : props.$caught ? '#ef4444' : 'rgba(0,0,0,0.5)'};
`;

export const DIFFICULTY_CONFIG = {
  easy: {
    label: 'Easy — 60% to escape',
    doorPosition: 60,
    description: 'Answer 60% of questions correctly to reach the door',
  },
  medium: {
    label: 'Medium — 70% to escape',
    doorPosition: 70,
    description: 'Answer 70% of questions correctly to reach the door',
  },
  hard: {
    label: 'Hard — 85% to escape',
    doorPosition: 85,
    description: 'Answer 85% of questions correctly to reach the door',
  },
};

export const PACMAN_START = 0;
export const MISTAKE_MARGIN = 3;
export const PACMAN_STEP = 4;
export const INITIAL_HUMAN_LEAD = MISTAKE_MARGIN * PACMAN_STEP;

/** Avatar position on chase track — synced with progress but always ahead of Pac-Man at start */
export const getAvatarChasePosition = (progressPercent) => {
  return Math.max(progressPercent, INITIAL_HUMAN_LEAD);
};

export const getCatchThreshold = (progressPercent, doorPosition) => {
  if (progressPercent >= doorPosition) return Infinity;
  return getAvatarChasePosition(progressPercent);
};

const PacManChaseBar = ({
  progressPercent = 0,
  pacManPosition = PACMAN_START,
  doorPosition,
  gameOutcome,
  difficultyLabel,
}) => {
  const humanPosition = getAvatarChasePosition(progressPercent);
  const hasEscaped = gameOutcome === 'escaped';
  const isCaught = gameOutcome === 'caught';
  const pacManHunting = !hasEscaped && !isCaught;
  const pacBehindAvatar = pacManPosition < humanPosition;

  return (
    <ChaseSection>
      <ChaseLabel>
        <span>🏃 Escape the Pac-Man!</span>
        <span>{difficultyLabel}</span>
      </ChaseLabel>
      <Track>
        <TrackDots />
        <Door
          style={{ left: `${doorPosition}%` }}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 200 }}
        >
          <div className="door-frame">
            <div className="door-opening" />
            <div className="door-knob" />
          </div>
        </Door>
        <Character
          animate={{ left: `${pacManPosition}%` }}
          transition={{ type: 'spring', stiffness: 120, damping: 15 }}
          style={{ zIndex: pacBehindAvatar ? 3 : 4 }}
        >
          <PacManSprite $active={pacManHunting}>
            <div className="body">
              <div className="eye" />
            </div>
          </PacManSprite>
        </Character>
        <Character
          animate={{ left: `${humanPosition}%`, opacity: hasEscaped && progressPercent >= doorPosition ? 0.85 : 1 }}
          transition={{ type: 'spring', stiffness: 120, damping: 15 }}
          style={{ zIndex: pacBehindAvatar ? 4 : 3 }}
        >
          <HumanSprite $running={!hasEscaped && progressPercent > 0}>
            <div className="head" />
            <div className="body" />
            <div className="legs">
              <div className="leg" />
              <div className="leg" />
            </div>
          </HumanSprite>
        </Character>
      </Track>
      {hasEscaped && (
        <StatusMessage $escaped initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          You reached the door! Pac-Man stopped chasing. Keep answering to increase your score.
        </StatusMessage>
      )}
      {isCaught && (
        <StatusMessage $caught initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          Game Over — Pac-Man caught you before you reached the door!
        </StatusMessage>
      )}
    </ChaseSection>
  );
};

export default PacManChaseBar;
